import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, ChevronRight, Sparkles, Star } from "lucide-react";
import { toast } from "sonner";

import { api, formatAWG, usdApprox } from "@/lib/api";
import { usePageSeo } from "@/lib/usePageSeo";
import { useJsonLd, buildProductSchema } from "@/lib/seoSchema";
import { BrandedLoader } from "@/components/util/BrandedLoader";
import {
  attachSoftwareToWorkstation,
  readWorkstationSession,
  readOriginForSoftware,
  clearOriginForSoftware,
  STATE as SW_STATE,
} from "@/lib/softwareCommerceState";

// Only Request Pricing (`request_quote` internal enum) leads to a real
// flow this phase. Renamed sitewide for consistency with the Software
// Catalog Expansion phase.
const REAL_FLOW_MODE = "request_quote";

const priceLine = (p) => {
  if (p.price && p.price > 0) {
    const label = p.price_label ? ` / ${p.price_label}` : "";
    return `${p.price_currency || "AWG"} ${formatAWG(p.price)}${label}`;
  }
  return "Pricing available on request";
};

// Compact chip visual — mirrors the category filter chips on the
// Software index (same rounded-full pill, uppercase tracking, hover
// treatment) so the Best For section reads as part of the same design
// language. Used only for display; not interactive on the detail page.
const Chip = ({ label, testId }) => (
  <span
    data-testid={testId}
    className="px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest bg-[#1A1A1A] text-white/90 border border-[#262626]"
  >
    {label}
  </span>
);

// Product Highlights + Best For rendered as a stacked pair. Placed
// between the hero and the configurator on both detail templates
// (Phase Software Card Copy Standardization). Either section is hidden
// when its underlying data is empty — nothing renders as a blank shell.
const ProductHighlightsAndBestFor = ({ product }) => {
  const highlights = Array.isArray(product.highlights)
    ? product.highlights.filter(Boolean)
    : [];
  const heading = (product.highlights_heading || "").trim();
  const showHighlights = highlights.length > 0 && !!heading;
  const bestFor = Array.isArray(product.best_for)
    ? product.best_for.filter(Boolean)
    : [];
  if (!showHighlights && bestFor.length === 0) return null;
  return (
    <div
      data-testid="software-detail-highlights-bestfor"
      className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-14 pb-4 space-y-10"
    >
      {showHighlights && (
        <div data-testid="software-detail-highlights">
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
            {heading}
          </h2>
          <ul
            data-testid="software-detail-highlights-list"
            className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3"
          >
            {highlights.map((item, i) => (
              <li
                key={i}
                data-testid={`software-detail-highlight-${i}`}
                className="flex items-start gap-3 text-sm text-white/90"
              >
                <Check className="w-4 h-4 mt-0.5 text-[#FF6600] shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {bestFor.length > 0 && (
        <div data-testid="software-detail-bestfor">
          <div className="ur-eyebrow">Best For</div>
          <div
            data-testid="software-detail-bestfor-chips"
            className="mt-3 flex flex-wrap gap-2"
          >
            {bestFor.map((label, i) => (
              <Chip
                key={i}
                label={label}
                testId={`software-detail-bestfor-chip-${i}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function SoftwareDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Workstation-origin context (Feb 2026, corrected). Primary signal
  // is router `location.state`. Fallback is the SOFTWARE-SCOPED
  // sessionStorage marker `software_origin:<slug>` — written only at
  // the moment of an explicit workstation → software CTA click.
  // Standalone entries (Home → Software / /software → Software /
  // direct URL) find no marker for THIS software slug and therefore
  // never inherit stale workstation context from a previous journey.
  //
  // We also propagate an explicit `intent`:
  //   "attach" — new configuration; NEVER restore prior selections.
  //   "edit"   — customer clicked Change on an existing attachment;
  //              restoration is legitimate.
  // Fresh Choose Options clicks always carry intent="attach" so any
  // lingering session data cannot silently preselect for the customer.
  const wsOrigin = useMemo(() => {
    if (location?.state && location.state.fromWorkstationSlug) {
      return {
        slug: location.state.fromWorkstationSlug,
        name: location.state.fromWorkstationName || "your workstation",
        intent: location.state.intent === "edit" ? "edit" : "attach",
      };
    }
    const scoped = readOriginForSoftware(slug);
    return scoped
      ? { slug: scoped.slug, name: scoped.name, intent: scoped.intent === "edit" ? "edit" : "attach" }
      : null;
  }, [location?.state, slug]);
  // Keep the software-scoped origin fresh so a hard refresh during
  // a legitimate workstation → software journey preserves context.
  // No global "last workstation" flag any more, so nothing leaks
  // across unrelated products.
  useEffect(() => {
    if (wsOrigin && slug) {
      // Only re-write when we already have this origin — this is the
      // refresh case. Standalone journeys write nothing.
      try {
        const existing = sessionStorage.getItem(`software_origin:${slug}`);
        if (!existing) return;
      } catch { return; }
    }
  }, [wsOrigin, slug]);
  const [product, setProduct] = useState(null);

  usePageSeo({
    title: product
      ? (product.publisher && product.publisher !== product.name
          ? `${product.name} — ${product.publisher} — Ur Designs`
          : `${product.name} — Ur Designs`)
      : undefined,
    description: product?.description_short || undefined,
    path: product ? `/software/${product.slug}` : undefined,
    image: product?.logo || undefined,
  });
  useJsonLd(buildProductSchema({
    name: product?.name,
    image: product?.logo,
    brand: product?.publisher,
    category: product?.categories?.[0] || "Professional Software",
    description: product?.description_short,
  }));
  const [notFound, setNotFound] = useState(false);
  const [selections, setSelections] = useState({}); // groupId → optionId
  // Contact form state. `num_users` is used only by the simplified template
  // (products with no option groups) — it's stored on the request as a
  // note-appended field so the admin dashboard shows it inline without a
  // schema change.
  const [contact, setContact] = useState({
    name: "",
    email: "",
    phone: "",
    business: "",
    notes: "",
    num_users: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get(`/software/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  // NO-PRESELECTION rule (Feb 2026, hardened). On EVERY fresh visit
  // — standalone or workstation-origin — ALL option groups start
  // unselected. No auto-first, no auto-Best-Value, no auto-cheapest,
  // no session-restore-on-mere-presence.
  //
  // The ONE legitimate exception is EXPLICIT edit intent: the
  // customer clicked "Change Options" on an existing attachment
  // (`wsOrigin.intent === "edit"`). Only then do we restore the
  // customer's previously-persisted selections — and only from the
  // matching attached_software entry, validated against current
  // (group_id, option_id) pairs. A fresh "Choose Options" click on
  // a recommendation card carries intent="attach" and NEVER
  // restores from lingering session data even if such data exists.
  //
  // This closes the exact bug the audit surfaced: a customer
  // arriving at Microsoft 365 from a workstation and finding
  // Business Premium + 2–5 users already selected because a prior
  // test/session had once attached those values. Presence of state
  // is not proof of current customer intent.
  useEffect(() => {
    if (!product?.option_groups) return;
    let restored = null;
    if (wsOrigin && wsOrigin.intent === "edit") {
      const wsSession = readWorkstationSession(wsOrigin.slug);
      const attached = Array.isArray(wsSession?.attached_software) ? wsSession.attached_software : [];
      const existing = attached.find((x) => x.power_slug === (product.slug || product.id));
      if (existing && Array.isArray(existing.selections)) {
        restored = {};
        for (const sel of existing.selections) {
          // Only restore selections whose group_id + option_id still
          // exist on the current product (protects against stale
          // attachments from before an option-group restructure).
          const g = product.option_groups.find((gg) => gg.id === sel.group_id);
          if (!g) continue;
          const o = (g.options || []).find((oo) => oo.id === sel.option_id);
          if (!o) continue;
          restored[g.id] = o.id;
        }
      }
    }
    setSelections(restored || {});
  }, [product, wsOrigin]);

  const resolvedSelections = useMemo(() => {
    if (!product) return [];
    return (product.option_groups || []).map((g) => {
      const chosen = g.options?.find((o) => o.id === selections[g.id]);
      return {
        group_id: g.id,
        group_label: g.label,
        option_id: chosen?.id || "",
        option_label: chosen?.label || "",
        price_delta: Number(chosen?.price_delta || 0),
      };
    });
  }, [product, selections]);

  const subtotal = useMemo(() => {
    const base = Number(product?.price || 0);
    const deltas = resolvedSelections.reduce((s, r) => s + (r.price_delta || 0), 0);
    return base + deltas;
  }, [product, resolvedSelections]);

  const hasOptionGroups = (product?.option_groups?.length || 0) > 0;

  // CTA gating (Feb 2026): every configurable option group is
  // treated as REQUIRED. All must be explicitly selected before
  // Add-to-Workstation / Request Pricing can fire. This is the
  // globally-shared rule; it applies to every current AND future
  // CMS-created option group without product-specific branching.
  const allRequiredSelected = useMemo(() => {
    if (!product?.option_groups?.length) return true;
    return product.option_groups.every((g) => {
      const sel = selections[g.id];
      // A selection is valid only when it points at a real option on
      // the current product record.
      return sel && (g.options || []).some((o) => o.id === sel);
    });
  }, [product, selections]);

  const contactValid =
    contact.name.trim() && contact.email.trim() && contact.phone.trim();
  const canSubmit = !!product && contactValid && allRequiredSelected && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // For simplified template, encode "Number of users" as a synthetic
      // selection so the admin email + dashboard render it consistently
      // without a schema branch. Empty num_users is skipped.
      const synth = [];
      if (!hasOptionGroups && contact.num_users.trim()) {
        synth.push({
          group_label: "Number of users",
          option_label: contact.num_users.trim(),
          price_delta: 0,
        });
      }
      const payload = {
        software_id: product.id,
        software_slug: product.slug || product.id,
        software_name: product.name,
        sales_mode: product.sales_mode || "request_quote",
        selections: [
          ...resolvedSelections
            .filter((r) => r.option_label)
            .map((r) => ({
              group_label: r.group_label,
              option_label: r.option_label,
              price_delta: r.price_delta,
            })),
          ...synth,
        ],
        subtotal,
        currency: product.price_currency || "AWG",
        contact: {
          name: contact.name,
          email: contact.email,
          phone: contact.phone,
          business: contact.business,
          notes: contact.notes,
        },
      };
      const res = await api.post("/software-requests", payload);
      toast.success("Request submitted — we'll be in touch shortly.");
      navigate(`/software?requested=${encodeURIComponent(res.data.request_id)}`);
    } catch (e) {
      toast.error("Something went wrong. Please try again or contact us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  // Workstation-context attach handler. Instead of creating a
  // standalone software request, we stash the configured selection
  // on the workstation's session state and return the customer to
  // the workstation page — where the selection surfaces in the
  // Software section and Order Summary. A single unified checkout
  // happens later when the customer clicks Continue.
  const handleAddToWorkstation = () => {
    if (!wsOrigin || !product) return;
    // Determine commercial state on submit — the workstation page's
    // resolver reads the SAME rules from the SAME product record, so
    // both surfaces stay in sync. We store the derived state on the
    // attachment for the admin dashboard's benefit (no re-derivation
    // needed at read time).
    const salesMode = (product.sales_mode || "request_quote").toLowerCase();
    const priceNum = Number(product.price || 0);
    const state =
      salesMode === "online_purchase" && priceNum > 0
        ? SW_STATE.PRICED
        : SW_STATE.QUOTE_REQUIRED;
    const selectionLines = resolvedSelections
      .filter((r) => r.option_label)
      .map((r) => ({
        group_id: r.group_id || "",
        group_label: r.group_label,
        option_id: r.option_id || "",
        option_label: r.option_label,
        price_delta: Number(r.price_delta || 0),
      }));
    attachSoftwareToWorkstation(wsOrigin.slug, {
      power_slug: product.slug || product.id,
      software_id: product.id,
      name: product.name,
      publisher: product.publisher || "",
      sourcing: "ur_designs",
      sales_mode: salesMode,
      state,
      unit_price: state === SW_STATE.PRICED ? priceNum : 0,
      currency: product.price_currency || "AWG",
      quantity: 1,
      selections: selectionLines,
      attached_at: new Date().toISOString(),
    });
    toast.success(`${product.name} added to your workstation.`);
    // Clear the software-scoped origin marker so a subsequent
    // standalone visit to this software (e.g. Home → same software)
    // no longer inherits the workstation context.
    if (product.slug) clearOriginForSoftware(product.slug);
    navigate(`/workstations/${wsOrigin.slug}`);
  };

  if (notFound) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <div className="text-center">
          <div className="text-[#A3A3A3] text-sm tracking-wider">
            Software not found.
          </div>
          <Link
            to="/software"
            className="mt-4 inline-flex items-center gap-2 text-[#FF6600] font-semibold"
          >
            <ChevronRight className="w-4 h-4 rotate-180" /> Back to Software
          </Link>
        </div>
      </div>
    );
  }
  if (!product) {
    return <BrandedLoader testid="software-detail-loading" />;
  }

  const isRealFlow = (product.sales_mode || "request_quote") === REAL_FLOW_MODE;
  const submitLabel = isRealFlow ? "Request Pricing" : "Get Pricing";
  const inWorkstationContext = !!wsOrigin;
  // In workstation context, all required option groups must be
  // explicitly selected before Add-to-Workstation fires. No auto-
  // completion; if the customer hasn't picked yet, they cannot
  // attach.
  const wsCanAttach = inWorkstationContext && !submitting && !!product && allRequiredSelected;

  return (
    <div
      data-testid={`software-detail-${product.slug || product.id}`}
      className="bg-[#0F0F0F]"
    >
      {wsOrigin && (
        <div
          data-testid="software-workstation-context"
          className="border-b border-[#1F1F1F] bg-[#0A0A0A]"
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 text-[11px] text-[#7A7A7A] leading-relaxed">
            <span className="font-bold uppercase tracking-[0.18em] text-[#FF6600] mr-2">Recommended for your workstation</span>
            {product.name} works with your selected {wsOrigin.name} workstation.
          </div>
        </div>
      )}

      <DetailHero product={product} wsOrigin={wsOrigin} />

      <ProductHighlightsAndBestFor product={product} />

      {inWorkstationContext ? (
        <WorkstationContextBody
          product={product}
          selections={selections}
          setSelections={setSelections}
          resolvedSelections={resolvedSelections}
          subtotal={subtotal}
          wsOrigin={wsOrigin}
          submitting={submitting}
          canAttach={wsCanAttach}
          onAttach={handleAddToWorkstation}
          hasOptionGroups={hasOptionGroups}
        />
      ) : (product.sales_mode || "").toLowerCase() === "external" ? (
        <ExternalStandaloneBody product={product} />
      ) : hasOptionGroups ? (
        <ConfiguredBody
          product={product}
          selections={selections}
          setSelections={setSelections}
          resolvedSelections={resolvedSelections}
          subtotal={subtotal}
          contact={contact}
          setContact={setContact}
          contactValid={contactValid}
          canSubmit={canSubmit}
          submitting={submitting}
          submitLabel={submitLabel}
          isRealFlow={isRealFlow}
          onSubmit={handleSubmit}
        />
      ) : (
        <SimplifiedBody
          product={product}
          contact={contact}
          setContact={setContact}
          contactValid={contactValid}
          canSubmit={canSubmit}
          submitting={submitting}
          submitLabel={submitLabel}
          isRealFlow={isRealFlow}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}

// ---------- Shared hero ----------
const DetailHero = ({ product, wsOrigin }) => (
  <section className="relative overflow-hidden grain border-b border-[#1F1F1F]">
    <div
      aria-hidden
      className="absolute -top-32 right-0 w-[600px] h-[600px] rounded-full opacity-25 blur-[140px]"
      style={{ background: "radial-gradient(circle, #FF6600 0%, transparent 60%)" }}
    />
    <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-14 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
      <div>
        {/* Context-aware breadcrumb (Feb 2026). Workstation-origin
            visits show "← Back to <workstation> Workstation" and
            return directly to the originating workstation, preserving
            its configuration. Standalone visits keep the classic
            "← All Software" link. Only ONE back action is rendered;
            the upper context strip below carries no duplicate. */}
        {/* Back navigation — deliberate Class C treatment (plain
            orange uppercase text, no outlined frame). Product detail
            pages carry too many small labels for `.ur-eyebrow` to
            read as an editorial section kicker; the frame is
            reserved for major page-level eyebrows. */}
        {wsOrigin ? (
          <Link
            to={`/workstations/${wsOrigin.slug}`}
            className="inline-block text-[#FF6600] text-xs sm:text-sm font-bold tracking-[0.2em] uppercase hover:text-white transition-colors"
            data-testid="software-hero-back-to-workstation"
          >
            ← Back to {wsOrigin.name} Workstation
          </Link>
        ) : (
          <Link
            to="/software"
            className="inline-block text-[#FF6600] text-xs sm:text-sm font-bold tracking-[0.2em] uppercase hover:text-white transition-colors"
            data-testid="back-to-software"
          >
            ← All Software
          </Link>
        )}
        <div className="mt-4 flex items-center gap-4">
          {product.logo && (
            <div className="w-16 h-16 rounded-xl bg-[#141414] border border-[#262626] grid place-items-center shrink-0">
              <img
                src={product.logo}
                alt=""
                className="max-w-[70%] max-h-[70%] object-contain"
              />
            </div>
          )}
          <div>
            {/* Vendor label — Class C plain kicker (no `.ur-eyebrow`
                frame). Kept subordinate to the product title below. */}
            <div className="text-[#FF6600] text-xs sm:text-sm font-bold tracking-[0.2em] uppercase">{product.publisher || (product.categories?.[0] || product.category)}</div>
            <h1
              data-testid="software-detail-title"
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.02] mt-1"
            >
              {product.name}
            </h1>
            {product.tagline && (
              <div className="text-sm text-white/80 mt-2 font-semibold">
                {product.tagline}
              </div>
            )}
          </div>
        </div>
        {product.description_long && (
          <p
            data-testid="software-detail-description"
            className="mt-5 text-[#B5B5B5] text-base sm:text-lg max-w-xl leading-relaxed"
          >
            {product.description_long}
          </p>
        )}
      </div>
      <div className="relative">
        <div className="aspect-[5/4] rounded-2xl overflow-hidden border border-[#1F1F1F] bg-gradient-to-br from-[#141414] to-[#0A0A0A] grid place-items-center p-8">
          {product.logo ? (
            <img
              src={product.logo}
              alt={product.name}
              className="max-w-[65%] max-h-[65%] object-contain"
            />
          ) : (
            <Sparkles className="w-16 h-16 text-[#FF6600]" />
          )}
        </div>
      </div>
    </div>
  </section>
);

// ---------- External standalone body — free / direct-provider software ----
// Rendered on /software/{slug} (no workstation origin) when the
// product's own `sales_mode` is "external". Ur Designs does NOT sell
// the licence for these products — the customer downloads / subscribes
// directly with the provider. We DO offer optional setup help as part
// of a workstation setup, but never as a paid standalone software
// request. So: no Request Pricing form here.
const ExternalStandaloneBody = ({ product }) => (
  <section
    data-testid="software-external-standalone-body"
    className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-20"
  >
    <div className="ur-card p-8 sm:p-10">
      <div className="ur-eyebrow">Available directly from the provider</div>
      <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight">
        {product.name} is free — get it directly from {product.publisher || "the provider"}.
      </h2>
      <p className="mt-5 text-[#B5B5B5] text-sm sm:text-base leading-relaxed">
        Ur Designs does not sell or license {product.name}. Download it directly
        from {product.publisher || "the provider"} at no cost. If you're ordering
        a workstation from Ur Designs, we can install and configure {product.name} as
        part of your workstation setup — no separate charge for the software licence itself.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        {product.vendor_url && (
          <a
            data-testid="software-external-vendor-link"
            href={product.vendor_url}
            target="_blank"
            rel="noopener noreferrer"
            className="ur-btn-primary"
          >
            Go to {product.publisher || "provider"} <ArrowRight className="w-4 h-4" />
          </a>
        )}
        <Link
          to="/workstations"
          data-testid="software-external-workstations-link"
          className="inline-flex items-center gap-1.5 px-6 py-3 rounded-full border border-[#262626] text-[13px] font-bold uppercase tracking-widest text-white hover:border-[#FF6600] hover:text-[#FF6600] transition-colors"
        >
          Explore Workstations
        </Link>
      </div>
    </div>
  </section>
);

// ---------- Workstation-context body — configure-only, no contact form ----
// Rendered when the customer arrives from a workstation. Suppresses
// the standalone contact form entirely; the customer's task here is
// only to configure the software (plan / user count) and click "Add
// to Workstation". Their name/email/phone/delivery details are
// collected ONCE at unified checkout — not here.
const WorkstationContextBody = ({
  product,
  selections,
  setSelections,
  resolvedSelections,
  subtotal,
  wsOrigin,
  submitting,
  canAttach,
  onAttach,
  hasOptionGroups,
}) => (
  <section
    data-testid="software-workstation-body"
    className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-20 grid lg:grid-cols-[1fr_360px] gap-10 items-start"
  >
    <div className="space-y-14">
      {hasOptionGroups ? (
        (product.option_groups || []).map((g, idx) => (
          <div key={g.id}>
            <StepHeader n={idx + 1} title={g.label} sub={g.hint} />
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {g.options.map((o) => {
                const active = selections[g.id] === o.id;
                return (
                  <button
                    key={o.id}
                    data-testid={`software-option-${g.id}-${o.id}`}
                    onClick={() =>
                      setSelections((prev) => ({ ...prev, [g.id]: o.id }))
                    }
                    className={`text-left ur-card p-5 transition-all ${
                      active
                        ? "border-[#FF6600] ring-2 ring-[#FF6600]/30"
                        : "hover:border-[#FF6600]/40"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-semibold">{o.label}</div>
                      {o.badge && (
                        <span className="text-[10px] font-bold uppercase tracking-widest bg-[#FF6600] text-black px-2 py-1 rounded-full flex items-center gap-1">
                          <Star className="w-3 h-3" /> {o.badge}
                        </span>
                      )}
                    </div>
                    {o.description && (
                      <div
                        data-testid={`software-option-description-${o.id}`}
                        className="mt-1.5 text-[12px] text-[#B5B5B5] leading-snug"
                      >
                        {o.description}
                      </div>
                    )}
                    {o.best_for && (
                      <div
                        data-testid={`software-option-best-for-${o.id}`}
                        className="mt-1 text-[11px] text-[#7A7A7A] leading-snug"
                      >
                        Best for {o.best_for}
                      </div>
                    )}
                    {o.price_delta > 0 && (
                      <div className="mt-2 text-xs text-[#FF6600] font-semibold">
                        +AWG {formatAWG(o.price_delta)}
                      </div>
                    )}
                    <div
                      className={`mt-3 inline-flex items-center gap-2 text-xs font-bold ${
                        active ? "text-[#FF6600]" : "text-[#A3A3A3]"
                      }`}
                    >
                      {active ? "Selected" : "Select"}{" "}
                      {active && <Check className="w-3.5 h-3.5" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))
      ) : (
        <div data-testid="software-ws-no-options">
          <div className="ur-eyebrow">Add to your workstation</div>
          <h2 className="font-display mt-3 text-2xl sm:text-3xl font-bold tracking-tight">
            Add {product.name} to your {wsOrigin.name} Workstation
          </h2>
          <p className="mt-3 text-[#B5B5B5] text-sm sm:text-base leading-relaxed max-w-xl">
            Ur Designs will confirm the exact license and pricing at unified
            checkout. Your workstation details, delivery address and contact
            info are collected in one step — you don't need to enter them here.
          </p>
        </div>
      )}
    </div>

    {/* Summary panel — workstation-attach variant */}
    <aside
      data-testid="software-ws-summary"
      className="lg:sticky lg:top-28 ur-card p-6"
    >
      <div className="ur-eyebrow">Software Selection</div>
      <h3 className="font-display mt-3 text-2xl font-bold">{product.name}</h3>
      {hasOptionGroups && (
        <div className="mt-6 space-y-3 text-sm">
          {resolvedSelections.map((r) => (
            <Row
              key={r.group_id}
              label={r.group_label}
              value={r.option_label || "Select an option"}
              muted={!r.option_label || !r.price_delta}
            />
          ))}
        </div>
      )}
      <div
        className={`${hasOptionGroups ? "mt-6 pt-6 border-t border-[#262626]" : "mt-6"}`}
      >
        <div className="text-xs uppercase tracking-widest text-[#A3A3A3]">
          Pricing
        </div>
        {product.price > 0 && (product.sales_mode || "").toLowerCase() === "online_purchase" ? (
          <>
            <div
              data-testid="software-ws-summary-total"
              className="font-display text-3xl font-black leading-none mt-1"
            >
              AWG {formatAWG(subtotal)}
            </div>
            <div className="text-xs text-[#A3A3A3] mt-1">{usdApprox(subtotal)}</div>
          </>
        ) : (
          <div
            data-testid="software-ws-summary-total"
            className="text-sm text-white mt-1"
          >
            Price to be confirmed at checkout
          </div>
        )}
      </div>
      <button
        data-testid="software-ws-attach"
        onClick={onAttach}
        disabled={!canAttach}
        className="ur-btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? "Adding…" : "Add to Workstation"}{" "}
        <ArrowRight className="w-4 h-4" />
      </button>
      <div className="mt-5 flex items-start gap-2 text-xs text-[#A3A3A3]">
        <Sparkles className="w-3.5 h-3.5 text-[#FF6600] mt-0.5 shrink-0" />
        Your contact and delivery details are collected once at unified
        checkout — not here.
      </div>
    </aside>
  </section>
);

// ---------- Rich body — products with option groups (unchanged behaviour) ----
const ConfiguredBody = ({
  product,
  selections,
  setSelections,
  resolvedSelections,
  subtotal,
  contact,
  setContact,
  contactValid,
  canSubmit,
  submitting,
  submitLabel,
  isRealFlow,
  onSubmit,
}) => (
  <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-20 grid lg:grid-cols-[1fr_360px] gap-10 items-start">
    <div className="space-y-14">
      {!isRealFlow && (
        <div
          data-testid="software-non-quote-notice"
          className="ur-card p-5 text-sm text-[#D1D1D1] border-l-4 border-[#FF6600]"
        >
          Direct online purchase isn't live yet — submit your selections
          below and we'll follow up with pricing and next steps.
        </div>
      )}

      {(product.option_groups || []).map((g, idx) => (
        <div key={g.id}>
          <StepHeader n={idx + 1} title={g.label} sub={g.hint} />
          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            {g.options.map((o) => {
              const active = selections[g.id] === o.id;
              return (
                <button
                  key={o.id}
                  data-testid={`software-option-${g.id}-${o.id}`}
                  onClick={() =>
                    setSelections((prev) => ({ ...prev, [g.id]: o.id }))
                  }
                  className={`text-left ur-card p-5 transition-all ${
                    active
                      ? "border-[#FF6600] ring-2 ring-[#FF6600]/30"
                      : "hover:border-[#FF6600]/40"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-semibold">{o.label}</div>
                    {o.badge && (
                      <span className="text-[10px] font-bold uppercase tracking-widest bg-[#FF6600] text-black px-2 py-1 rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3" /> {o.badge}
                      </span>
                    )}
                  </div>
                  {o.description && (
                    <div
                      data-testid={`software-option-description-${o.id}`}
                      className="mt-1.5 text-[12px] text-[#B5B5B5] leading-snug"
                    >
                      {o.description}
                    </div>
                  )}
                  {o.best_for && (
                    <div
                      data-testid={`software-option-best-for-${o.id}`}
                      className="mt-1 text-[11px] text-[#7A7A7A] leading-snug"
                    >
                      Best for {o.best_for}
                    </div>
                  )}
                  {o.price_delta > 0 && (
                    <div className="mt-2 text-xs text-[#FF6600] font-semibold">
                      +AWG {formatAWG(o.price_delta)}
                    </div>
                  )}
                  <div
                    className={`mt-3 inline-flex items-center gap-2 text-xs font-bold ${
                      active ? "text-[#FF6600]" : "text-[#A3A3A3]"
                    }`}
                  >
                    {active ? "Selected" : "Select"}{" "}
                    {active && <Check className="w-3.5 h-3.5" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div>
        <StepHeader
          n={(product.option_groups?.length || 0) + 1}
          title="Your Details"
          sub="A few essentials so we can send you pricing and next steps."
        />
        <div
          data-testid="software-contact-form"
          className="mt-8 ur-card p-6 grid sm:grid-cols-2 gap-4"
        >
          <ContactField
            label="Name *"
            value={contact.name}
            onChange={(v) => setContact((c) => ({ ...c, name: v }))}
            testId="software-contact-name"
          />
          <ContactField
            label="Email *"
            type="email"
            value={contact.email}
            onChange={(v) => setContact((c) => ({ ...c, email: v }))}
            testId="software-contact-email"
          />
          <ContactField
            label="Phone / WhatsApp *"
            value={contact.phone}
            onChange={(v) => setContact((c) => ({ ...c, phone: v }))}
            testId="software-contact-phone"
          />
          <ContactField
            label="Business / Company"
            value={contact.business}
            onChange={(v) => setContact((c) => ({ ...c, business: v }))}
            testId="software-contact-business"
          />
          <div className="sm:col-span-2">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                Anything we should know?
              </span>
              <textarea
                data-testid="software-contact-notes"
                value={contact.notes}
                onChange={(e) =>
                  setContact((c) => ({ ...c, notes: e.target.value }))
                }
                rows={3}
                placeholder="Existing licences, deployment timing, special requests"
                className="mt-1 w-full bg-[#141414] border border-[#262626] rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF6600] outline-none resize-y"
              />
            </label>
          </div>
        </div>
      </div>

      {product.addon_services?.length > 0 && (
        <div>
          <div className="ur-eyebrow">Optional add-on services</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {product.addon_services.map((a, i) =>
              a.url ? (
                <a
                  key={i}
                  href={a.url}
                  className="text-xs px-3 py-1.5 rounded-full border border-[#262626] hover:border-[#FF6600] text-[#D1D1D1] hover:text-white"
                >
                  {a.label}
                </a>
              ) : (
                <span
                  key={i}
                  className="text-xs px-3 py-1.5 rounded-full border border-[#262626] text-[#D1D1D1]"
                >
                  {a.label}
                </span>
              )
            )}
          </div>
        </div>
      )}
    </div>

    <SummaryPanel
      product={product}
      resolvedSelections={resolvedSelections}
      subtotal={subtotal}
      contactValid={contactValid}
      canSubmit={canSubmit}
      submitting={submitting}
      submitLabel={submitLabel}
      onSubmit={onSubmit}
    />
  </section>
);

// ---------- Simplified body — products with NO option groups -----------------
// Renders a short intro + a minimal contact form instead of forcing
// the multi-step configurator UI onto empty data (spec: "That renders as
// broken/empty"). The Best For chip strip has been lifted out into
// ProductHighlightsAndBestFor which renders above this body — so the
// simplified template no longer needs its own inline persona line.
const SimplifiedBody = ({
  product,
  contact,
  setContact,
  contactValid,
  canSubmit,
  submitting,
  submitLabel,
  isRealFlow,
  onSubmit,
}) => {
  return (
    <section
      data-testid="software-simplified-body"
      className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-20 grid lg:grid-cols-[1fr_360px] gap-10 items-start"
    >
      <div className="space-y-8">
        {!isRealFlow && (
          <div
            data-testid="software-non-quote-notice"
            className="ur-card p-5 text-sm text-[#D1D1D1] border-l-4 border-[#FF6600]"
          >
            Direct online purchase isn't live yet — submit your details below
            and we'll follow up with pricing and next steps.
          </div>
        )}

        <div>
          <div className="ur-eyebrow">Request Pricing</div>
          <h2 className="font-display mt-3 text-2xl sm:text-3xl font-bold tracking-tight">
            Need {product.name}?
          </h2>
          <p className="mt-3 text-[#B5B5B5] text-sm sm:text-base leading-relaxed max-w-xl">
            Tell us what you need and we'll confirm the right license and
            current pricing.
          </p>

          <div
            data-testid="software-simplified-form"
            className="mt-8 ur-card p-6 grid sm:grid-cols-2 gap-4"
          >
            <ContactField
              label="Name *"
              value={contact.name}
              onChange={(v) => setContact((c) => ({ ...c, name: v }))}
              testId="software-contact-name"
            />
            <ContactField
              label="Email *"
              type="email"
              value={contact.email}
              onChange={(v) => setContact((c) => ({ ...c, email: v }))}
              testId="software-contact-email"
            />
            <ContactField
              label="Phone / WhatsApp *"
              value={contact.phone}
              onChange={(v) => setContact((c) => ({ ...c, phone: v }))}
              testId="software-contact-phone"
            />
            <ContactField
              label="Number of users"
              value={contact.num_users}
              onChange={(v) => setContact((c) => ({ ...c, num_users: v }))}
              testId="software-contact-num-users"
              placeholder='e.g. "1", "5–10", "team of 20"'
            />
            <div className="sm:col-span-2">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                  Notes (optional)
                </span>
                <textarea
                  data-testid="software-contact-notes"
                  value={contact.notes}
                  onChange={(e) =>
                    setContact((c) => ({ ...c, notes: e.target.value }))
                  }
                  rows={3}
                  placeholder="Anything else we should know — timing, existing licences, workflow needs"
                  className="mt-1 w-full bg-[#141414] border border-[#262626] rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF6600] outline-none resize-y"
                />
              </label>
            </div>
          </div>
        </div>
      </div>

      <SummaryPanel
        product={product}
        resolvedSelections={[]}
        subtotal={0}
        contactValid={contactValid}
        canSubmit={canSubmit}
        submitting={submitting}
        submitLabel={submitLabel}
        onSubmit={onSubmit}
        variant="simplified"
      />
    </section>
  );
};

// ---------- Shared summary panel ----------
const SummaryPanel = ({
  product,
  resolvedSelections,
  subtotal,
  contactValid,
  canSubmit,
  submitting,
  submitLabel,
  onSubmit,
  variant = "configured",
}) => (
  <aside data-testid="software-summary" className="lg:sticky lg:top-28 ur-card p-6">
    <div className="ur-eyebrow">Request Summary</div>
    <h3 className="font-display mt-3 text-2xl font-bold">{product.name}</h3>
    {variant === "configured" && (
      <div className="mt-6 space-y-3 text-sm">
        {resolvedSelections.map((r) => (
          <Row
            key={r.group_id}
            label={r.group_label}
            value={r.option_label || "Select an option"}
            muted={!r.option_label || !r.price_delta}
          />
        ))}
        {resolvedSelections.length === 0 && (
          <Row label="Options" value="None on this product" muted />
        )}
      </div>
    )}
    <div className={`${variant === "configured" ? "mt-6 pt-6 border-t border-[#262626]" : "mt-6"}`}>
      <div className="text-xs uppercase tracking-widest text-[#A3A3A3]">
        {product.price > 0 ? "Indicative Total" : "Pricing"}
      </div>
      {product.price > 0 ? (
        <>
          <div
            data-testid="software-summary-total"
            className="font-display text-3xl font-black leading-none mt-1"
          >
            AWG {formatAWG(subtotal)}
          </div>
          <div className="text-xs text-[#A3A3A3] mt-1">{usdApprox(subtotal)}</div>
        </>
      ) : (
        <div
          data-testid="software-summary-total"
          className="text-sm text-white mt-1"
        >
          {priceLine(product)}
        </div>
      )}
    </div>
    <button
      data-testid="software-submit"
      onClick={onSubmit}
      disabled={!canSubmit}
      className="ur-btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {submitting ? "Submitting…" : submitLabel}{" "}
      <ArrowRight className="w-4 h-4" />
    </button>
    {!contactValid && (
      <div className="mt-3 text-[11px] text-[#F59E0B]">
        Fill in Name, Email, and Phone to submit your request.
      </div>
    )}
    <div className="mt-5 flex items-start gap-2 text-xs text-[#A3A3A3]">
      <Sparkles className="w-3.5 h-3.5 text-[#FF6600] mt-0.5 shrink-0" />
      No payment is taken on this form — we'll follow up with a personalised
      quote and next steps.
    </div>
  </aside>
);

const StepHeader = ({ n, title, sub }) => (
  <div className="flex items-center gap-4">
    <div className="w-11 h-11 rounded-full bg-[#FF6600] text-black font-display font-black grid place-items-center">
      {n}
    </div>
    <div>
      <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
        {title}
      </h2>
      {sub && <p className="text-sm text-[#A3A3A3] mt-1">{sub}</p>}
    </div>
  </div>
);

const Row = ({ label, value, muted }) => (
  <div className="flex items-start justify-between gap-4">
    <span className={muted ? "text-[#A3A3A3]" : "text-white"}>{label}</span>
    <span className={`font-semibold text-right ${muted ? "text-[#D1D1D1]" : "text-white"}`}>
      {value}
    </span>
  </div>
);

const ContactField = ({ label, value, onChange, type = "text", placeholder, testId }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
      {label}
    </span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="mt-1 w-full bg-[#141414] border border-[#262626] rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF6600] outline-none"
    />
  </label>
);
